import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  SimpleGrid,
  Select,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stat,
  StatLabel,
  StatNumber,
  Divider,
  Progress,
  Badge,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { usageApi, UsageRow } from '../services/api';
import SubscribeModal from '../components/SubscribeModal';

const fmtUsd = (v: number) => `$${v.toFixed(v < 1 ? 4 : 2)}`;
const fmtNum = (v: number) => v.toLocaleString('pt-BR');

// Rótulos amigáveis dos processos.
const processLabel: Record<string, string> = {
  embedding: 'Embeddings',
  chat: 'Chat',
  classifier: 'Classificador (regulatório)',
  answerer: 'Resposta regulatória',
  vision: 'Análise de imagem',
  transcription: 'Transcrição (áudio)',
};

const UsageTable = ({
  title,
  rows,
  labelFor,
}: {
  title: string;
  rows: UsageRow[];
  labelFor?: (key: string) => string;
}) => (
  <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
    <Heading size="sm" mb={3} color="gray.700">
      {title}
    </Heading>
    {rows.length === 0 ? (
      <Text fontSize="sm" color="gray.400">
        Sem consumo no período.
      </Text>
    ) : (
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Item</Th>
            <Th isNumeric>Chamadas</Th>
            <Th isNumeric>Tokens</Th>
            <Th isNumeric>Custo</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((r) => (
            <Tr key={r.key}>
              <Td>{labelFor ? labelFor(r.key) : r.key}</Td>
              <Td isNumeric>{fmtNum(r.calls)}</Td>
              <Td isNumeric>{fmtNum(r.tokens)}</Td>
              <Td isNumeric>{fmtUsd(r.cost_usd)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    )}
  </Box>
);

const Usage = () => {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery(['usage-summary', days], () => usageApi.summary(days));
  const { data: balance } = useQuery('openrouter-balance', usageApi.balance, { staleTime: 60000 });
  const { data: plan } = useQuery('plan-status', usageApi.plan, { staleTime: 60000 });
  const { isOpen, onOpen, onClose } = useDisclosure();

  const pct = (used: number, max: number) => (max > 0 ? Math.min(100, (used / max) * 100) : 0);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1}>
        <Heading size="lg" color="brand.700">
          Governança de IA
        </Heading>
        <Select value={days} onChange={(e) => setDays(Number(e.target.value))} size="sm" w="auto">
          <option value={7}>Últimos 7 dias</option>
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
        </Select>
      </Flex>
      <Text color="gray.600" mb={6}>
        Consumo de IA (chamadas, tokens e custo estimado) por processo e por usuário da sua organização.
      </Text>

      {plan && (
        <Box borderWidth="1px" borderRadius="lg" p={4} mb={6} bg="white" shadow="sm">
          <Flex justify="space-between" align="center" mb={3}>
            <HStack>
              <Text fontWeight="semibold">Plano</Text>
              <Badge colorScheme="brand">{plan.label}</Badge>
            </HStack>
            <HStack>
              {!plan.ai_within_budget && <Badge colorScheme="red">Cota de IA atingida</Badge>}
              <Button size="sm" colorScheme="brand" onClick={onOpen}>
                {plan.plan === 'trial' ? 'Assinar um plano' : 'Mudar plano'}
              </Button>
            </HStack>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Usuários — {plan.usage.users}/{plan.limits.max_users}
              </Text>
              <Progress value={pct(plan.usage.users, plan.limits.max_users)} size="sm" borderRadius="full" colorScheme="brand" />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>
                Coleções — {plan.usage.collections}/{plan.limits.max_collections}
              </Text>
              <Progress value={pct(plan.usage.collections, plan.limits.max_collections)} size="sm" borderRadius="full" colorScheme="brand" />
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500" mb={1}>
                IA no mês — {fmtUsd(plan.usage.ai_spend_month_usd)}/{fmtUsd(plan.limits.monthly_ai_budget_usd)}
              </Text>
              <Progress
                value={pct(plan.usage.ai_spend_month_usd, plan.limits.monthly_ai_budget_usd)}
                size="sm"
                borderRadius="full"
                colorScheme={plan.ai_within_budget ? 'green' : 'red'}
              />
            </Box>
          </SimpleGrid>
        </Box>
      )}

      <SubscribeModal isOpen={isOpen} onClose={onClose} />

      {balance && balance.remaining != null && (
        <Box
          borderWidth="1px"
          borderRadius="lg"
          p={4}
          mb={6}
          bg={balance.remaining < 3 ? 'red.50' : balance.remaining < 10 ? 'orange.50' : 'green.50'}
          borderColor={balance.remaining < 3 ? 'red.200' : balance.remaining < 10 ? 'orange.200' : 'green.200'}
        >
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Saldo real da conta OpenRouter
          </Text>
          <HStack spacing={8}>
            <Stat>
              <StatLabel color="gray.500">Restante</StatLabel>
              <StatNumber color={balance.remaining < 3 ? 'red.600' : 'gray.800'}>{fmtUsd(balance.remaining)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.500">Gasto</StatLabel>
              <StatNumber>{fmtUsd(balance.used || 0)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel color="gray.500">Total</StatLabel>
              <StatNumber>{fmtUsd(balance.total || 0)}</StatNumber>
            </Stat>
          </HStack>
          {balance.remaining < 10 && (
            <Text fontSize="xs" color="red.600" mt={1}>
              Saldo baixo — recarregue em openrouter.ai/settings/credits.
            </Text>
          )}
        </Box>
      )}

      {isLoading || !data ? (
        <Flex justify="center" py={16}>
          <Spinner color="brand.500" />
        </Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Stat>
                <StatLabel color="gray.500">Custo estimado</StatLabel>
                <StatNumber color="brand.700">{fmtUsd(data.total.cost_usd)}</StatNumber>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Stat>
                <StatLabel color="gray.500">Tokens</StatLabel>
                <StatNumber>{fmtNum(data.total.tokens)}</StatNumber>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Stat>
                <StatLabel color="gray.500">Chamadas</StatLabel>
                <StatNumber>{fmtNum(data.total.calls)}</StatNumber>
              </Stat>
            </Box>
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
            <UsageTable title="Por processo" rows={data.by_process} labelFor={(k) => processLabel[k] || k} />
            <UsageTable title="Por modelo" rows={data.by_model} />
            <UsageTable title="Por grupo" rows={data.by_group} />
            <UsageTable title="Por usuário" rows={data.by_user} />
          </SimpleGrid>

          {/* Detalhe processo × modelo (qual processo, em qual modelo, quanto custou) */}
          <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
            <Heading size="sm" mb={3} color="gray.700">
              Detalhe: processo × modelo
            </Heading>
            {data.by_process_model.length === 0 ? (
              <Text fontSize="sm" color="gray.400">
                Sem consumo no período.
              </Text>
            ) : (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Processo</Th>
                    <Th>Modelo</Th>
                    <Th isNumeric>Chamadas</Th>
                    <Th isNumeric>Tokens</Th>
                    <Th isNumeric>Custo</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.by_process_model.map((r, i) => (
                    <Tr key={i}>
                      <Td>{processLabel[r.process] || r.process}</Td>
                      <Td fontSize="xs" color="gray.600">{r.model}</Td>
                      <Td isNumeric>{fmtNum(r.calls)}</Td>
                      <Td isNumeric>{fmtNum(r.tokens)}</Td>
                      <Td isNumeric>{fmtUsd(r.cost_usd)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>

          <Divider my={6} />
          <Text fontSize="xs" color="gray.400">
            Custo estimado pela tabela de preços por modelo (entrada/saída). Processos: embeddings, chat,
            classificador (LLM filter), resposta regulatória, análise de imagem e transcrição. A medição "por
            grupo" entra quando os grupos de usuários forem habilitados.
          </Text>
        </>
      )}
    </Box>
  );
};

export default Usage;
